/**
 * FNSP (Family and Neighborhood Support Partnership) Eligibility Service
 * TypeScript functions that call the FNSP API endpoints
 */
import { apiGet, apiPost } from './api';
import {
  FNSPEligibilityStatus,
  FNSPAssessment,
  FNSPAssessmentRequest,
  FNSPQuestionsResponse,
} from '../types/fnsp';

/**
 * Get FNSP eligibility status for an individual
 * Returns whether assessment exists and eligibility status
 */
export async function getFNSPEligibilityStatus(
  individualId: number
): Promise<FNSPEligibilityStatus> {
  const response = await apiGet<FNSPEligibilityStatus>(
    `/individuals/${individualId}/fnsp/eligibility`
  );

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    console.error('Fetch FNSP eligibility error:', { individualId, error: response.error });
    throw new Error(serverMsg || 'Unable to load FNSP eligibility status. Please try again.');
  }

  return response.data;
}

/**
 * Get the full FNSP assessment for an individual
 */
export async function getFNSPAssessment(individualId: number): Promise<FNSPAssessment | null> {
  const response = await apiGet<FNSPAssessment>(`/individuals/${individualId}/fnsp`);

  // 404 means no assessment exists yet
  if (!response.success) {
    if (response.error?.code === 'NOT_FOUND' || response.error?.message?.includes('not found')) {
      return null;
    }
    const serverMsg = response.error?.message || '';
    console.error('Fetch FNSP assessment error:', { individualId, error: response.error });
    throw new Error(serverMsg || 'Unable to load FNSP assessment. Please try again.');
  }

  return response.data;
}

/**
 * Create or update FNSP eligibility assessment for an individual
 */
export async function saveFNSPAssessment(
  individualId: number,
  data: FNSPAssessmentRequest
): Promise<FNSPAssessment> {
  const response = await apiPost<FNSPAssessment>(`/individuals/${individualId}/fnsp`, data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    console.error('Save FNSP assessment error:', { individualId, error: response.error });
    throw new Error(serverMsg || 'Unable to save FNSP assessment. Please try again.');
  }

  return response.data;
}

/**
 * Get FNSP questionnaire questions configuration
 */
export async function getFNSPQuestions(): Promise<FNSPQuestionsResponse> {
  const response = await apiGet<FNSPQuestionsResponse>('/fnsp/questions');

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    console.error('Fetch FNSP questions error:', response.error);
    throw new Error(serverMsg || 'Unable to load FNSP questions. Please try again.');
  }

  return response.data;
}
